import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getTenantSlugFromPath } from '@/lib/tenant';

const API = `${window.location.origin}/api`;

// Path-based multi-tenancy: tokens are namespaced per tenant so a single
// browser can hold valid sessions for multiple salons simultaneously.
const TENANT_SLUG = getTenantSlugFromPath();
const TOKEN_KEY = TENANT_SLUG ? `salonos_token_${TENANT_SLUG}` : 'salonos_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    // Set auth header immediately so other API calls work during verify
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    axios.get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setUser({
          email: res.data.email,
          tenantId: res.data.tenant_id,
          tenantName: res.data.tenant_name,
          isPrimary: !!res.data.is_primary,
        });
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.success) {
      const t = res.data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setUser({
        email: res.data.email,
        name: res.data.name,
        tenantId: res.data.tenant_id,
        tenantName: res.data.tenant_name,
        isPrimary: false, // verify call updates this on next mount
      });
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      // Fire-and-forget refresh of tenant flags
      axios.get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${t}` } })
        .then(r => setUser(prev => prev ? { ...prev, isPrimary: !!r.data.is_primary, tenantId: r.data.tenant_id, tenantName: r.data.tenant_name } : prev))
        .catch(() => {});
      return true;
    }
    return false;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Ignore logout API errors
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
